/**
 * StateTransitionPanel.jsx
 * ─────────────────────────
 * Panel de transiciones contextual basado en /api/cases/{id}/state-info.
 *
 * Props:
 *   • caseId         (string, required)
 *   • currentState   (string, required) — para optimistic UI
 *   • onTransitioned (fn(updatedCase)) — callback tras transición OK
 *   • endpoint       (string, default "/admin/cases") — para variar role
 *
 * Renderiza un botón por cada `allowed_targets_for_role` del state-info.
 * Cada botón abre un modal de confirmación con un campo opcional de razón.
 */
import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { ArrowRight, AlertTriangle } from "lucide-react";

// Etiquetas amistosas para cada estado destino
const STATE_LABELS = {
  draft_intake: "Borrador",
  nivel0_pending: "Prevalidación pendiente",
  nivel0_ready: "Explorador listo",
  out_of_scope: "Fuera de alcance",
  nivel1_payment_pending: "Pago Diagnóstico pendiente",
  nivel1_paid: "Pago Diagnóstico confirmado",
  nivel1_processing: "Procesando Diagnóstico",
  nivel1_needs_human_review: "Revisión humana N1",
  nivel1_revision_requested: "Revisión solicitada N1",
  nivel1_delivered: "Diagnóstico entregado",
  nivel2_offer_sent: "Oferta Kit enviada",
  nivel2_payment_pending: "Pago Kit pendiente",
  nivel2_paid: "Pago Kit confirmado",
  nivel2_processing: "Procesando Kit",
  nivel2_needs_human_review: "Revisión humana N2",
  nivel2_revision_requested: "Revisión solicitada N2",
  nivel2_delivered: "Kit entregado",
  closed: "Cerrar caso",
};

// Tono visual sugerido por estado
const STATE_TONE = {
  closed: "danger",
  out_of_scope: "danger",
  nivel1_revision_requested: "warning",
  nivel2_revision_requested: "warning",
  nivel1_delivered: "success",
  nivel2_delivered: "success",
  nivel1_paid: "success",
  nivel2_paid: "success",
};

// Estados que requieren obligatoriamente razón (motivo)
const REASON_REQUIRED_STATES = new Set([
  "closed",
  "out_of_scope",
  "nivel1_revision_requested",
  "nivel2_revision_requested",
]);

export default function StateTransitionPanel({
  caseId,
  currentState,
  onTransitioned,
  endpoint = "/admin/cases",
}) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState(null); // target state being confirmed
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/state-info`);
      setInfo(data);
    } catch (e) {
      // No bloquear UI; solo loguear
      console.warn("state-info load failed", e);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load, currentState]);

  const onTransition = async () => {
    if (!confirmTarget) return;
    const requiresReason = REASON_REQUIRED_STATES.has(confirmTarget);
    const trimmed = (reason || "").trim();
    if (requiresReason && trimmed.length < 10) {
      toast.error("Indica una razón de al menos 10 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `${endpoint}/${caseId}/transition`,
        { target_state: confirmTarget, ...(trimmed ? { reason: trimmed } : {}) },
      );
      toast.success(`Transición ejecutada → ${STATE_LABELS[confirmTarget] || confirmTarget}`);
      setConfirmTarget(null);
      setReason("");
      onTransitioned?.(data);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo ejecutar la transición");
    } finally {
      setSubmitting(false);
    }
  };

  const targets = info?.allowed_targets_for_role || [];

  if (loading) {
    return <div className="text-xs text-gray-400 italic">Cargando transiciones…</div>;
  }
  if (targets.length === 0) {
    return (
      <div className="text-xs text-gray-500 italic" data-testid="no-transitions">
        Sin transiciones disponibles desde <code className="bg-gray-100 px-1">{currentState}</code> para tu rol.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="state-transition-panel">
      <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">
        Transiciones disponibles ({targets.length})
      </div>
      <div className="flex flex-wrap gap-2">
        {targets.map((t) => {
          const tone = STATE_TONE[t] || "default";
          const toneCls = {
            default: "border-gray-300 text-gray-800 hover:bg-gray-50",
            success: "border-emerald-400 text-emerald-900 hover:bg-emerald-50",
            warning: "border-amber-400 text-amber-900 hover:bg-amber-50",
            danger: "border-rose-400 text-rose-900 hover:bg-rose-50",
          }[tone];
          return (
            <Button
              key={t}
              variant="outline"
              size="sm"
              className={`rounded-sm ${toneCls}`}
              onClick={() => setConfirmTarget(t)}
              data-testid={`transition-btn-${t}`}
            >
              <ArrowRight className="w-3 h-3 mr-1" /> {STATE_LABELS[t] || t}
            </Button>
          );
        })}
      </div>

      <Dialog
        open={!!confirmTarget}
        onOpenChange={(o) => { if (!o && !submitting) { setConfirmTarget(null); setReason(""); } }}
      >
        <DialogContent className="max-w-md" data-testid="transition-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${REASON_REQUIRED_STATES.has(confirmTarget) ? "text-amber-600" : "text-gray-400"}`} />
              Confirmar transición
            </DialogTitle>
            <DialogDescription asChild>
              <div>
                <code className="bg-gray-100 px-1">{currentState}</code>
                <span className="mx-1">→</span>
                <code className="bg-brand-cream text-brand-burgundy px-1">{confirmTarget}</code>
                <div className="mt-1 text-gray-700">
                  {STATE_LABELS[confirmTarget] || confirmTarget}
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-3 space-y-2">
            <Label htmlFor="trans-reason">
              Razón / motivo {REASON_REQUIRED_STATES.has(confirmTarget) && <span className="text-rose-600">*</span>}
            </Label>
            <Textarea
              id="trans-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={
                REASON_REQUIRED_STATES.has(confirmTarget)
                  ? "Obligatorio: explica brevemente por qué (mín 10 chars). Queda en audit log."
                  : "Opcional. Queda en audit log."
              }
              data-testid="transition-reason"
            />
            <div className="text-[11px] text-gray-500">
              {reason.length} caracteres
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setConfirmTarget(null); setReason(""); }}
              disabled={submitting}
              data-testid="transition-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={onTransition}
              disabled={submitting || (REASON_REQUIRED_STATES.has(confirmTarget) && reason.trim().length < 10)}
              className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
              data-testid="transition-confirm"
            >
              {submitting ? "Aplicando…" : "Confirmar transición"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
